import type {
  Bootstrap,
  TenantIdentity,
  Limits,
  Me,
  Model,
  Playing,
  Preset,
  Quota,
  Row,
  SocketEvent,
  StageStatus,
  Stats,
  Subscription,
  TuberState,
  Voice,
  Accent,
} from "./types";

/* Everything the server owns lives here. The panel is a projection of one
   bootstrap payload plus the socket's event stream, so the reducer is
   essentially the socket's switch statement with the DOM writes removed. */

export const HISTORY_CAP = 120;

export interface State {
  me: Me | null;
  tenant: TenantIdentity;
  voices: Voice[];
  models: Model[];
  limits: Limits;
  defaults: Bootstrap["defaults"];
  history: Row[];
  /* The instance's shared characters, as this user is allowed to see them. */
  presets: Preset[];
  presetsOnly: boolean;
  locked: boolean;
  quota: Quota | null;
  stats: Stats | null;
  subscription: Subscription | null;
  /* One entry per browser source that is sounding right now. */
  nowPlaying: Playing[];
  online: string[];
  overlays: number;
  overlaySeenAt: number | null;
  stages: StageStatus[];
  volume: number;
  /* The main overlay's character. A preset's own character rides on the preset
     itself, so the two never have to be kept in step with each other. */
  tuber: TuberState | null;
  overlayUrl: string | null;
  lastAudioUrl: string | null;
  /* Starred clips, oldest pad first. Kept apart from `history` because the
     history is a capped window and a pad from last month is not in it. */
  soundboard: Row[];
  accent: Accent;
}

export const initialState: State = {
  me: null,
  tenant: { slug: "", display_name: "" },
  voices: [],
  models: [],
  limits: {},
  defaults: { voice_id: "", model_id: "", output_format: "" },
  history: [],
  presets: [],
  presetsOnly: false,
  locked: false,
  quota: null,
  stats: null,
  subscription: null,
  nowPlaying: [],
  online: [],
  overlays: 0,
  overlaySeenAt: null,
  stages: [],
  volume: 1,
  tuber: null,
  overlayUrl: null,
  lastAudioUrl: null,
  soundboard: [],
  accent: "blue",
};

export type Action =
  | { type: "bootstrap"; data: Bootstrap }
  | { type: "socket"; frame: SocketEvent }
  | { type: "voices"; voices: Voice[] }
  | { type: "presets"; presets: Preset[] }
  | { type: "subscription"; subscription: Subscription }
  | { type: "quota"; quota: Quota }
  | { type: "limits"; limits: Limits }
  | { type: "tuber"; tuber: TuberState }
  | { type: "volume"; volume: number };

function upsert(history: Row[], row: Row): Row[] {
  const index = history.findIndex((existing) => existing.id === row.id);
  const next =
    index >= 0
      ? history.map((existing, at) => (at === index ? row : existing))
      : [row, ...history];
  return next.slice(0, HISTORY_CAP);
}

/* A pad joins the end of the board when it is starred and keeps its place when
   it is renamed; unstarring takes it off. */
function syncSoundboard(soundboard: Row[], row: Row): Row[] {
  const index = soundboard.findIndex((pad) => pad.id === row.id);
  if (!row.starred) return index >= 0 ? soundboard.filter((pad) => pad.id !== row.id) : soundboard;
  return index >= 0
    ? soundboard.map((pad, at) => (at === index ? row : pad))
    : [...soundboard, row];
}

function newestAudio(history: Row[]): string | null {
  return history.find((row) => row.audio_url)?.audio_url ?? null;
}

export function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "bootstrap": {
      const data = action.data;
      return {
        ...state,
        me: data.me,
        tenant: data.tenant,
        voices: data.voices,
        models: data.models,
        limits: data.limits,
        defaults: data.defaults,
        history: data.history,
        presets: data.presets,
        presetsOnly: data.presets_only,
        locked: data.locked,
        quota: data.quota,
        stats: data.stats,
        subscription: data.subscription,
        nowPlaying: data.now_playing,
        online: data.online,
        overlays: data.overlays_connected,
        overlaySeenAt: data.overlay_seen_at,
        stages: data.stages,
        volume: data.volume,
        tuber: data.tuber,
        overlayUrl: data.overlay_url,
        lastAudioUrl: newestAudio(data.history),
        soundboard: data.soundboard ?? [],
        accent: data.appearance?.accent ?? "blue",
      };
    }

    case "voices":
      return { ...state, voices: action.voices };
    case "presets":
      return { ...state, presets: action.presets };
    case "subscription":
      return { ...state, subscription: action.subscription };
    case "quota":
      return { ...state, quota: action.quota };
    case "limits":
      return { ...state, limits: action.limits };
    case "tuber":
      return { ...state, tuber: action.tuber };
    case "volume":
      return { ...state, volume: action.volume };

    case "socket": {
      const frame = action.frame;
      switch (frame.event) {
        case "request.new":
        case "request.update": {
          const history = upsert(state.history, frame.data);
          return {
            ...state,
            history,
            soundboard: syncSoundboard(state.soundboard, frame.data),
            // A preview is not "the last clip" until it has been sent: the
            // button that plays it lives on the preview itself.
            lastAudioUrl:
              frame.data.status === "held"
                ? state.lastAudioUrl
                : (frame.data.audio_url ?? state.lastAudioUrl),
          };
        }
        case "request.delete":
          return {
            ...state,
            history: state.history.filter((row) => row.id !== frame.data.id),
            soundboard: state.soundboard.filter((row) => row.id !== frame.data.id),
          };
        case "history.clear":
          // A purge keeps the soundboard's rows on the server, so it keeps them
          // here too rather than showing an empty board until the next reload.
          return {
            ...state,
            history: state.history.filter((row) => row.starred),
            lastAudioUrl: null,
          };
        case "appearance":
          return { ...state, accent: frame.data.accent };
        case "now_playing":
          return { ...state, nowPlaying: frame.data };
        case "stats":
          return { ...state, stats: frame.data };
        case "subscription":
          return { ...state, subscription: frame.data };
        case "locked":
          return {
            ...state,
            locked: frame.data.locked,
            presetsOnly: frame.data.presets_only,
          };
        case "presence":
          return {
            ...state,
            online: frame.data.online,
            overlays: frame.data.overlays,
            overlaySeenAt: frame.data.overlay_seen_at,
            stages: frame.data.stages,
          };
        case "volume":
          // Only the main overlay's volume lives here; a preset's own volume is
          // part of the preset, and its frame is addressed to that stage.
          return frame.data.stage == null ? { ...state, volume: frame.data.volume } : state;
        case "tuber":
          // Every character change arrives on the same event, tagged with the
          // stage it belongs to: the instance's own, or one preset's.
          return frame.data.stage == null
            ? { ...state, tuber: frame.data }
            : {
                ...state,
                presets: state.presets.map((preset) =>
                  preset.id === frame.data.stage
                    ? { ...preset, tuber: frame.data }
                    : preset,
                ),
              };
        default:
          // "toast" is side-effect only; handled where the frame is received.
          return state;
      }
    }

    default:
      return state;
  }
}
